import { SourcePlatformType } from "@analytics/generated/graphql";

enum Trigger {
  ADD_TO_CART,
  BUY_NOW,
}

export enum Events {
  PRODUCT_VIEW = "product_view",
  ADD_TO_CART = "add_to_cart",
  ADD_TO_WISHLIST = "add_to_wishlist",
  REMOVE_ITEM_FROM_CART = "remove_from_cart",
  CART = "cart",
  USER = "user",
  WISHLIST = "wishlist",
  PRODUCT_IMPRESSION = "product_impression",
  EVENT_IMPRESSION = "event_impression",
  PRODUCT_GROUP_IMPRESSION = "product_group_impression",
  RECOMMENDER_IMPRESSION = "recommender_impression",
  PRODUCT_LIST_INTERACTION = "product_list_interaction",
  RECOMMENDER_REQUEST = "recommender_request",
  EVENT_CLICK = "event_click",
}

type CommonOptions = {
  source_id?: string | null;
  source_type?: number | null;
  source_position?: number | null;
  source_platform?: SourcePlatformType;
  offer_id?: string;
  event_id?: number | string | null;
  position?: number;
};

export type ProductViewContext = {
  context: Events.PRODUCT_VIEW;
  options: Pick<
    CommonOptions,
    "offer_id" | "source_id" | "source_position" | "source_type"
  >;
};

export type AddToCartContext = {
  context: Events.ADD_TO_CART;
  options: CommonOptions & {
    customer_id?: string;
    trigger?: Trigger;
    success?: boolean;
    srtrev?: string;
  };
};

export type RemoveItemFromCartContext = {
  context: Events.REMOVE_ITEM_FROM_CART;
  options: CommonOptions & {
    customer_id?: string;
    shipping?: number;
  };
};

export type CartContext = {
  context: Events.CART;
};

export type UserContext = {
  context: Events.USER;
};

export type WishlistContext = {
  context: Events.WISHLIST;
  options: {
    page_type?: string;
    product_id?: string;
  };
};

export type AddToWishlistContext = {
  context: Events.ADD_TO_WISHLIST;
  options: Pick<CommonOptions, "source_type" | "event_id" | "position"> & {
    // Wishlist ID
    wishlist?: string;
    quantity?: number;
    event_title?: string;
  };
};

export type FromProduct = {
  from_product_ids: number[] | null;
  from_product_offer_id: number | null;
  from_product_displayed_price: number | null;
};

export type ProductImpressionContext = {
  context: Events.PRODUCT_IMPRESSION;
  options: Pick<
    CommonOptions,
    "source_id" | "source_position" | "source_type"
  > &
    FromProduct & {
      offer_id: number;
      product_id: number;
      product_title: string;
      displayed_price: number | null;
    };
};

export type EventImpressionContext = {
  context: Events.EVENT_IMPRESSION;
  options: Pick<
    CommonOptions,
    "source_id" | "event_id" | "source_type" | "position"
  > &
    FromProduct & {
      badge_text: string | null;
      event_type: string;
      position: number;
      schedule_entry_id: number | null;
      subtitle: string | null;
      title: string;
    };
};

export type ProductGroupImpressionContext = {
  context: Events.PRODUCT_GROUP_IMPRESSION;
  options: Pick<CommonOptions, "source_type"> & {
    product_count: number;
    displayed_group_price: number | null;
  };
};

export type RecommenderImpressionContext = {
  context: Events.RECOMMENDER_IMPRESSION;
  options: Pick<CommonOptions, "source_type"> &
    FromProduct & {
      recommended_product_count: number;
    };
};

export type ProductListInteractionType = "click" | "option" | "addToCart";

export type ProductListInteractionContext = {
  context: Events.PRODUCT_LIST_INTERACTION;
  options: Pick<
    CommonOptions,
    "source_id" | "source_type" | "source_position"
  > &
    FromProduct & {
      interaction?: number;
      category?: string | null;
      category_secondary?: string | null;
      category_tertiary?: string | null;
      product_id: number;
      offer_id: number;
      srtrev?: string | null;
    };
};

export type RecommenderRequestContext = {
  context: Events.RECOMMENDER_REQUEST;
  options: Pick<CommonOptions, "source_type"> &
    FromProduct & {
      pre_hydration_product_count?: number | null;
      post_hydration_product_count?: number | null;
      hydration_error_count?: number | null;
      rec_version?: number;
      cold_start_version?: number | null;
    };
};

export type EventClickContext = {
  context: Events.EVENT_CLICK;
  options: Pick<CommonOptions, "source_type" | "position"> &
    FromProduct & {
      title: string | null;
      subtitle?: string | null;
      badge_text?: string | null;
      event_id: number;
      position: number;
      event_type: string;
      schedule_entry_id: number;
      click_target: "event" | "product";
    };
};
